import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  // Placeholder data when database is empty
  const placeholderTestimonials = [
    {
      id: '1',
      client_name: 'Sarah Johnson',
      company: 'TechStart Inc.',
      service: 'Business Consultancy',
      testimonial:
        'EclipseProfessionals transformed our business strategy. Their insights helped us optimize operations and increase revenue by 40%. Highly recommended!',
      rating: 5,
    },
    {
      id: '2',
      client_name: 'Michael Chen',
      company: 'Urban Fashion Co.',
      service: 'Graphic Design & Branding',
      testimonial:
        'The branding work was exceptional. They captured our vision perfectly and created a visual identity that truly represents our brand.',
      rating: 5,
    },
    {
      id: '3',
      client_name: 'Emma Williams',
      company: 'Digital Nomad Hub',
      service: 'Digital Content',
      testimonial:
        'Our social media engagement tripled after working with EclipseProfessionals. Their content strategy was exactly what we needed.',
      rating: 5,
    },
  ];

  const displayTestimonials = testimonials.length > 0 ? testimonials : placeholderTestimonials;

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={i < rating ? 'fill-brand-orange text-brand-orange' : 'text-gray-600'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-eclipse-bg">
      {/* Hero Section */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at top right, rgba(0, 217, 255, 0.15), transparent 40%), radial-gradient(circle at bottom left, rgba(255, 140, 66, 0.1), transparent 40%)',
        }}
        data-testid="testimonials-hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan mb-4">
              Client Feedback
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6">
              What Our{' '}
              <span className="bg-gradient-to-r from-brand-cyan to-brand-orange bg-clip-text text-transparent">
                Clients Say
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-eclipse-secondary leading-relaxed">
              Don't just take our word for it. Here's what our clients have to say about working with
              us.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-24 md:py-32" data-testid="testimonials-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-cyan"></div>
              <p className="text-eclipse-secondary mt-4">Loading testimonials...</p>
            </div>
          ) : displayTestimonials.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayTestimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className="group bg-eclipse-surface border border-white/5 rounded-2xl p-8 hover:-translate-y-1 hover:border-brand-cyan/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.1)] transition-all duration-300"
                  data-testid={`testimonial-card-${index + 1}`}
                >
                  <div className="mb-4">{renderStars(testimonial.rating)}</div>
                  <p className="text-eclipse-text italic leading-relaxed mb-6">
                    "{testimonial.testimonial}"
                  </p>
                  <div className="border-t border-white/5 pt-4">
                    <p className="font-semibold text-eclipse-text">{testimonial.client_name}</p>
                    <p className="text-sm text-eclipse-secondary">{testimonial.company}</p>
                    <p className="text-xs text-brand-cyan mt-2 uppercase tracking-wider">
                      {testimonial.service}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="testimonials-empty-state">
              <div className="inline-flex p-6 bg-eclipse-surface rounded-full mb-6">
                <MessageSquare className="w-12 h-12 text-brand-cyan" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Testimonials Coming Soon</h3>
              <p className="text-eclipse-secondary max-w-md mx-auto">
                We're gathering feedback from our amazing clients. Check back soon to read their
                experiences!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at center, rgba(0, 217, 255, 0.1), transparent 50%)',
        }}
        data-testid="testimonials-cta-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Ready to Join Our Happy Clients?
          </h2>
          <p className="text-eclipse-secondary text-lg leading-relaxed mb-8">
            Experience the EclipseProfessionals difference. Let's start your success story today.
          </p>
          <Link
            to="/contact"
            data-testid="testimonials-contact-cta"
            className="inline-flex items-center gap-2 bg-brand-cyan text-eclipse-bg font-bold px-10 py-5 rounded-full hover:bg-white hover:scale-105 transition-all duration-300 text-lg"
          >
            Work With Us
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;